import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { ActivityFeed } from '../ActivityFeed'
import { CalculationConsole } from '../bottom/CalculationConsole'
import { ValidationPanel } from '../bottom/ValidationPanel'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'
import { Activity, Terminal, AlertTriangle, ChevronDown } from 'lucide-react'

interface BottomPanelProps {
  onCollapse: () => void
  events: ToolCallEvent[]
  connected: boolean
  connecting: boolean
  onClearEvents: () => void
  activities: ActivityWithExchanges[]
  onSelectNode?: (nodeId: string, nodeType: 'activity' | 'exchange') => void
}

export function BottomPanel({
  onCollapse,
  events,
  connected,
  connecting,
  onClearEvents,
  activities,
  onSelectNode,
}: BottomPanelProps) {
  const { bottomTab, setBottomTab } = useWorkspaceStore()

  return (
    <div className="h-full flex flex-col bg-background border-t border-border min-h-0">
      <Tabs
        value={bottomTab}
        onValueChange={(v) => setBottomTab(v as 'feed' | 'console' | 'validation')}
        className="flex-1 flex flex-col min-h-0"
      >
        <div className="flex items-center justify-between px-2 py-1 border-b border-border bg-surface-sunken/50">
          <TabsList className="h-7 p-0.5 bg-muted/50">
            <TabsTrigger
              value="feed"
              className="h-6 px-2 text-xs gap-1 data-[state=active]:bg-background"
            >
              <Activity className="h-3 w-3" />
              Activity
              {events.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-primary/20 text-primary rounded-full">
                  {events.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="console"
              className="h-6 px-2 text-xs gap-1 data-[state=active]:bg-background"
            >
              <Terminal className="h-3 w-3" />
              Console
            </TabsTrigger>
            <TabsTrigger
              value="validation"
              className="h-6 px-2 text-xs gap-1 data-[state=active]:bg-background"
            >
              <AlertTriangle className="h-3 w-3" />
              Validation
            </TabsTrigger>
          </TabsList>

          <button
            onClick={onCollapse}
            className="p-1 hover:bg-accent rounded-sm transition-colors"
            title="Collapse panel"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <TabsContent value="feed" className="flex-1 m-0 overflow-hidden min-h-0">
          <div className="h-full p-2 overflow-y-auto scrollbar-visible">
            <ActivityFeed
              events={events}
              connected={connected}
              connecting={connecting}
              onClear={onClearEvents}
              compact
            />
          </div>
        </TabsContent>

        <TabsContent value="console" className="flex-1 m-0 overflow-hidden min-h-0">
          <CalculationConsole events={events} />
        </TabsContent>

        <TabsContent value="validation" className="flex-1 m-0 overflow-hidden min-h-0">
          <ValidationPanel activities={activities} onSelectNode={onSelectNode} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
